/**
 * Voice messages: record a short clip from the microphone.
 *
 * The microphone is opened when recording starts and released the instant it
 * stops, is cancelled, or anything goes wrong — iOS shows its orange recording
 * indicator for as long as any app holds the mic, so every path must close it.
 *
 * Codec: Safari only produces audio/mp4 (AAC), Chrome/Firefox produce WebM or
 * Ogg Opus. The *actual* type the recorder used is what travels with the
 * message — a WebM clip labelled audio/mp4 is unplayable at the other end.
 */
import { setAudioSessionType, isIosDevice } from "./callAudio.js";
import { getMicStream, stopStream } from "./webrtc.js";

/** Hard cap — a voice note is a message, not a podcast (and must fit the 25MB attachment limit). */
export const MAX_VOICE_MS = 120_000;
/** Shorter than this is a mis-tap, not a message. */
export const MIN_VOICE_MS = 700;

const PREFERRED_TYPES = [
  "audio/mp4",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
];

export function voiceRecordingSupported(): boolean {
  return typeof MediaRecorder !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function";
}

/** The first type this browser can record, or "" to let it choose for itself. */
export function pickVoiceMimeType(
  isSupported: (type: string) => boolean = (type) =>
    typeof MediaRecorder !== "undefined" && typeof MediaRecorder.isTypeSupported === "function"
      ? MediaRecorder.isTypeSupported(type)
      : false,
): string {
  return PREFERRED_TYPES.find(isSupported) ?? "";
}

export interface VoiceRecording {
  blob: Blob;
  mimeType: string;
  durationMs: number;
}

export interface VoiceRecorderHandle {
  /** Finish and hand over the clip. Releases the microphone. */
  stop(): Promise<VoiceRecording>;
  /** Throw the clip away. Releases the microphone. */
  cancel(): void;
  elapsedMs(): number;
}

let active: VoiceRecorderHandle | null = null;

export function isRecordingVoice(): boolean {
  return active !== null;
}

/** File extension matching what the recorder actually produced. */
export function voiceFileExtension(mimeType: string): string {
  if (mimeType.includes("mp4") || mimeType.includes("mpeg") || mimeType.includes("aac")) return "m4a";
  if (mimeType.includes("ogg")) return "ogg";
  return "webm";
}

/**
 * Start recording. `onAutoStop` fires if the cap is reached, so the caller can
 * send (or discard) a clip it didn't explicitly stop.
 */
export async function startVoiceRecording(
  onAutoStop: (recording: VoiceRecording) => void,
): Promise<VoiceRecorderHandle> {
  if (active) throw new Error("Already recording.");
  if (!voiceRecordingSupported()) throw new Error("This browser can't record audio.");

  let released = false;
  let stream: MediaStream | null = null;
  const release = () => {
    if (released) return;
    released = true;
    stopStream(stream);
    stream = null;
    if (isIosDevice()) setAudioSessionType("auto");
    if (active === handle) active = null;
  };

  // Declared before `release` runs so the closure above can reference it.
  let handle: VoiceRecorderHandle;

  try {
    // Before the request, not after: iOS refuses to capture while the audio
    // session is in a playback category.
    if (isIosDevice()) setAudioSessionType("play-and-record");
    stream = await getMicStream();

    const mimeType = pickVoiceMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const chunks: BlobPart[] = [];
    recorder.addEventListener("dataavailable", (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    });

    const startedAt = Date.now();
    let capTimer: ReturnType<typeof setTimeout> | null = null;
    let finished: Promise<VoiceRecording> | null = null;

    const finish = (): Promise<VoiceRecording> => {
      finished ??= new Promise<VoiceRecording>((resolve, reject) => {
        const settle = () => {
          if (capTimer) clearTimeout(capTimer);
          capTimer = null;
          // What the recorder actually used — never the type we asked for.
          const firstChunkType = chunks[0] instanceof Blob ? chunks[0].type : "";
          const type = recorder.mimeType || firstChunkType || mimeType || "audio/mp4";
          const blob = new Blob(chunks, { type });
          release();
          if (blob.size === 0) reject(new Error("Nothing was recorded."));
          else resolve({ blob, mimeType: type, durationMs: Date.now() - startedAt });
        };
        recorder.addEventListener("stop", settle, { once: true });
        recorder.addEventListener(
          "error",
          () => {
            release();
            reject(new Error("Recording failed."));
          },
          { once: true },
        );
        try {
          if (recorder.state !== "inactive") recorder.stop();
          else settle();
        } catch (err) {
          release();
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      });
      return finished;
    };

    handle = {
      stop: finish,
      cancel() {
        if (capTimer) clearTimeout(capTimer);
        capTimer = null;
        try {
          if (recorder.state !== "inactive") recorder.stop();
        } catch {
          /* already stopped */
        }
        release();
      },
      elapsedMs: () => Date.now() - startedAt,
    };

    capTimer = setTimeout(() => {
      capTimer = null;
      void finish().then(onAutoStop, () => undefined);
    }, MAX_VOICE_MS);

    // No timeslice: iOS writes a playable file only when it finalizes in one go.
    recorder.start();
    active = handle;
    return handle;
  } catch (err) {
    release();
    throw err;
  }
}

/** Leaving the app mid-recording: keep what was captured, but free the mic. */
export function stopRecordingOnHide(onSalvaged: (recording: VoiceRecording) => void): void {
  const onHidden = () => {
    if (document.visibilityState !== "hidden" || !active) return;
    void active.stop().then(onSalvaged, () => undefined);
  };
  document.addEventListener("visibilitychange", onHidden);
  window.addEventListener("pagehide", onHidden);
}
